import { useState } from 'react';

type Tab = 'overview' | 'models' | 'usage' | 'keys' | 'providers' | 'pricing';

type AetherModel = {
  id: string;
  name: string;
  family: string;
  provider: string;
  context: string;
  capabilities: string[];
  status: 'active' | 'degraded' | 'maintenance';
  pricing: string;
  tier: string;
};

type UsageStat = {
  model: string;
  requests: number;
  tokens: number;
  cost: string;
  latency: string;
};

const MODELS: AetherModel[] = [
  {
    id: 'aether-orion-v1',
    name: 'Orion',
    family: 'Qwen3.5-122B-A10B',
    provider: 'OpenRouter',
    context: '1M',
    capabilities: ['Coding', 'Creative', 'Tools'],
    status: 'active',
    pricing: '$0.435/1M in',
    tier: 'developer',
  },
  {
    id: 'aether-vega-v1',
    name: 'Vega',
    family: 'Qwen3-30B-A3B',
    provider: 'OpenRouter',
    context: '2M',
    capabilities: ['Reasoning', 'Research', 'Analysis'],
    status: 'active',
    pricing: '$0.87/1M in',
    tier: 'developer',
  },
  {
    id: 'aether-atlas-v1',
    name: 'Atlas',
    family: 'Qwen2.5-Coder-32B',
    provider: 'Ollama',
    context: '128K',
    capabilities: ['Agentic', 'Tool Use', 'Orchestration'],
    status: 'active',
    pricing: 'Free (local)',
    tier: 'developer',
  },
  {
    id: 'aether-nova-v1',
    name: 'Nova',
    family: 'Qwen2.5-VL-32B',
    provider: 'OpenRouter',
    context: '128K',
    capabilities: ['Vision', 'Multimodal', 'OCR'],
    status: 'active',
    pricing: '$0.50/1M in',
    tier: 'developer',
  },
  {
    id: 'aether-draco-v1',
    name: 'Draco',
    family: 'Qwen2.5-Coder-32B',
    provider: 'Ollama',
    context: '128K',
    capabilities: ['Security', 'Code Review', 'Audit'],
    status: 'active',
    pricing: 'Free (local)',
    tier: 'team',
  },
  {
    id: 'aether-lyra-v1',
    name: 'Lyra',
    family: 'Whisper-Large-v3',
    provider: 'OpenRouter',
    context: '32K',
    capabilities: ['Speech', 'TTS', 'Transcription'],
    status: 'active',
    pricing: '$0.10/1K sec',
    tier: 'developer',
  },
  {
    id: 'aether-code-v1',
    name: 'Code',
    family: 'DeepSeek-Coder-33B',
    provider: 'OpenRouter',
    context: '128K',
    capabilities: ['Code Gen', 'Debug', 'Refactor'],
    status: 'active',
    pricing: '$0.35/1M in',
    tier: 'free',
  },
  {
    id: 'aether-hunter-v1',
    name: 'Hunter',
    family: 'Hunter-Alpha',
    provider: 'OpenRouter',
    context: '128K',
    capabilities: ['Pen Test', 'Vuln Scan', 'Exploit'],
    status: 'active',
    pricing: '$0.50/1M in',
    tier: 'team',
  },
  {
    id: 'aether-healer-v1',
    name: 'Healer',
    family: 'Healer-Alpha',
    provider: 'OpenRouter',
    context: '128K',
    capabilities: ['Debug', 'Fix', 'Optimize'],
    status: 'active',
    pricing: '$0.35/1M in',
    tier: 'developer',
  },
  {
    id: 'aether-embed-v1',
    name: 'Embed',
    family: 'Nomic-Embed-Text-v1.5',
    provider: 'Ollama',
    context: '8K',
    capabilities: ['Embeddings', 'Search', 'RAG'],
    status: 'active',
    pricing: 'Free (local)',
    tier: 'free',
  },
  {
    id: 'aether-search-v1',
    name: 'Search',
    family: 'BGE-Base-EN-v1.5',
    provider: 'Ollama',
    context: '8K',
    capabilities: ['Search', 'Rerank', 'Retrieval'],
    status: 'active',
    pricing: 'Free (local)',
    tier: 'free',
  },
  {
    id: 'aether-mini-v1',
    name: 'Mini',
    family: 'Qwen2.5-7B',
    provider: 'Ollama',
    context: '32K',
    capabilities: ['Lightweight', 'Fast', 'Edge'],
    status: 'active',
    pricing: 'Free (local)',
    tier: 'free',
  },
];

const USAGE: UsageStat[] = [
  { model: 'aether-orion-v1', requests: 1247, tokens: 8942300, cost: '$3.89', latency: '1.2s' },
  { model: 'aether-vega-v1', requests: 892, tokens: 12567800, cost: '$10.93', latency: '2.4s' },
  { model: 'aether-atlas-v1', requests: 3456, tokens: 12340000, cost: 'Free', latency: '0.8s' },
  { model: 'aether-nova-v1', requests: 234, tokens: 456700, cost: '$0.23', latency: '3.1s' },
  { model: 'aether-embed-v1', requests: 5678, tokens: 56780000, cost: 'Free', latency: '0.3s' },
];

const API_KEYS = [
  {
    key: 'sk-ae-...a1b2',
    name: 'Production App',
    created: '2026-06-01',
    usage: '45,230 req',
    lastUsed: '2m ago',
    tier: 'team',
  },
  {
    key: 'sk-ae-...c3d4',
    name: 'Dev Environment',
    created: '2026-06-05',
    usage: '2,340 req',
    lastUsed: '15m ago',
    tier: 'developer',
  },
  {
    key: 'sk-ae-...e5f6',
    name: 'CI/CD Pipeline',
    created: '2026-06-08',
    usage: '890 req',
    lastUsed: '1h ago',
    tier: 'developer',
  },
];

const s = {
  fc: { display: 'flex' as const, alignItems: 'center' as const },
  card: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12,
  },
  input: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#f5f5f5',
    fontSize: 13,
    fontFamily: "'Inter', sans-serif",
    width: '100%',
    outline: 'none' as const,
  },
};

function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  color?: string;
}) {
  return (
    <div style={{ ...s.card, padding: '20px 24px', flex: 1, minWidth: 160 }}>
      <div
        style={{
          fontSize: 11,
          color: 'rgba(255,255,255,0.35)',
          letterSpacing: '1px',
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 700,
          fontFamily: "'Rajdhani', sans-serif",
          color: color || '#D6A94E',
        }}
      >
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>{sub}</div>
      )}
    </div>
  );
}

function Badge({ text, color }: { text: string; color: string }) {
  return (
    <span
      style={{
        fontSize: 10,
        padding: '2px 8px',
        borderRadius: 4,
        background: `${color}15`,
        color,
        fontWeight: 600,
      }}
    >
      {text}
    </span>
  );
}

export default function App() {
  const [tab, setTab] = useState<Tab>('overview');
  const nav = [
    { id: 'overview' as Tab, label: 'Overview', icon: '⊞' },
    { id: 'models' as Tab, label: 'Models', icon: '◆' },
    { id: 'usage' as Tab, label: 'Usage', icon: '◰' },
    { id: 'keys' as Tab, label: 'API Keys', icon: '⚙' },
    { id: 'providers' as Tab, label: 'Providers', icon: '⇧' },
    { id: 'pricing' as Tab, label: 'Pricing', icon: '$' },
  ];
  const providerColors: Record<string, string> = {
    OpenRouter: '#3b82f6',
    Ollama: '#22c55e',
    HuggingFace: '#f59e0b',
    GitHub: '#6e40c9',
    Docker: '#0db7ed',
  };

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        background: '#050505',
        color: '#f5f5f5',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <aside
        style={{
          width: 220,
          background: '#0a0a0a',
          borderRight: '1px solid rgba(255,255,255,0.04)',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
        }}
      >
        <div
          style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
        >
          <div style={{ ...s.fc, gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'linear-gradient(135deg,#D6A94E,#FF6A1A)',
                ...s.fc,
                justifyContent: 'center',
                fontSize: 18,
                fontWeight: 700,
                color: '#050505',
              }}
            >
              Λ
            </div>
            <div>
              <div
                style={{
                  fontFamily: "'Rajdhani', sans-serif",
                  fontSize: 17,
                  fontWeight: 700,
                  letterSpacing: '1px',
                  lineHeight: 1.2,
                }}
              >
                LLM Platform
              </div>
              <div
                style={{
                  fontSize: 9,
                  color: 'rgba(255,255,255,0.3)',
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                }}
              >
                Model Management
              </div>
            </div>
          </div>
        </div>
        <nav style={{ flex: 1, padding: '12px 8px' }}>
          {nav.map((item) => (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '10px 14px',
                borderRadius: 8,
                cursor: 'pointer',
                border: 'none',
                fontSize: 13,
                fontWeight: 500,
                background: tab === item.id ? 'rgba(214,169,78,0.08)' : 'none',
                color: tab === item.id ? '#D6A94E' : 'rgba(255,255,255,0.5)',
                fontFamily: "'Inter', sans-serif",
                textAlign: 'left',
              }}
            >
              <span style={{ fontSize: 14, width: 20, textAlign: 'center' }}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>
        <div
          style={{
            padding: '16px 20px',
            borderTop: '1px solid rgba(255,255,255,0.04)',
            fontSize: 11,
            color: 'rgba(255,255,255,0.2)',
            lineHeight: 1.6,
          }}
        >
          <div>12 models active</div>
          <div>4 providers connected</div>
        </div>
      </aside>

      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <header
          style={{
            ...s.fc,
            justifyContent: 'space-between',
            padding: '14px 28px',
            borderBottom: '1px solid rgba(255,255,255,0.04)',
            background: 'rgba(5,5,5,0.8)',
            backdropFilter: 'blur(12px)',
            flexShrink: 0,
          }}
        >
          <h1
            style={{
              fontFamily: "'Rajdhani', sans-serif",
              fontSize: 20,
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            {nav.find((n) => n.id === tab)?.label}
          </h1>
          <div style={{ ...s.fc, gap: 8 }}>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>
              All systems operational
            </span>
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#22c55e',
                boxShadow: '0 0 6px rgba(34,197,94,0.5)',
              }}
            />
          </div>
        </header>

        <div style={{ flex: 1, overflowY: 'auto', padding: 28 }}>
          {tab === 'overview' && (
            <div style={{ maxWidth: 1000 }}>
              <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
                <StatCard
                  label="Active Models"
                  value="12"
                  sub="Across 4 providers"
                  color="#D6A94E"
                />
                <StatCard
                  label="Today's Requests"
                  value="11,507"
                  sub="~45M tokens"
                  color="#3b82f6"
                />
                <StatCard
                  label="Today's Cost"
                  value="$15.05"
                  sub="OpenRouter billed"
                  color="#f59e0b"
                />
                <StatCard label="Uptime" value="99.97%" sub="30-day average" color="#22c55e" />
              </div>

              <div style={{ ...s.card, padding: 24, marginBottom: 24 }}>
                <h2
                  style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontSize: 16,
                    fontWeight: 600,
                    marginBottom: 16,
                    color: '#D6A94E',
                  }}
                >
                  Top Models by Usage
                </h2>
                {USAGE.map((u) => {
                  const model = MODELS.find((m) => m.id === u.model);
                  return (
                    <div
                      key={u.model}
                      style={{
                        ...s.fc,
                        gap: 14,
                        padding: '10px 0',
                        borderBottom: '1px solid rgba(255,255,255,0.03)',
                      }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          background: 'rgba(214,169,78,0.1)',
                          ...s.fc,
                          justifyContent: 'center',
                          fontSize: 14,
                          color: '#D6A94E',
                        }}
                      >
                        {model?.name[0]}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>
                          {model?.name || u.model}
                        </div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
                          {u.requests.toLocaleString()} req · {u.cost}
                        </div>
                      </div>
                      <div style={{ width: 160 }}>
                        <div
                          style={{
                            height: 4,
                            background: 'rgba(255,255,255,0.06)',
                            borderRadius: 2,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              height: '100%',
                              width: `${(u.requests / 6000) * 100}%`,
                              background: '#3b82f6',
                              borderRadius: 2,
                            }}
                          />
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: 12,
                          color: 'rgba(255,255,255,0.4)',
                          width: 60,
                          textAlign: 'right',
                        }}
                      >
                        {u.latency}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div style={{ ...s.card, padding: 24 }}>
                <h2
                  style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontSize: 16,
                    fontWeight: 600,
                    marginBottom: 16,
                    color: '#D6A94E',
                  }}
                >
                  Provider Status
                </h2>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 12,
                  }}
                >
                  {[
                    { name: 'OpenRouter', status: 'Connected', models: 8, color: '#3b82f6' },
                    { name: 'Ollama (Local)', status: 'Connected', models: 4, color: '#22c55e' },
                    { name: 'HuggingFace', status: 'Standby', models: 0, color: '#f59e0b' },
                    { name: 'GitHub Models', status: 'Blocked', models: 0, color: '#ef4444' },
                  ].map((p) => (
                    <div
                      key={p.name}
                      style={{
                        padding: 16,
                        borderRadius: 10,
                        border: `1px solid ${p.color}20`,
                        background: `${p.color}06`,
                      }}
                    >
                      <div style={{ ...s.fc, gap: 8, marginBottom: 8 }}>
                        <span
                          style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }}
                        />
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                        {p.status} · {p.models} models
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === 'models' && (
            <div style={{ maxWidth: 1000 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 16,
                }}
              >
                {MODELS.map((m) => (
                  <div
                    key={m.id}
                    style={{
                      ...s.card,
                      padding: 20,
                      borderLeft: `3px solid ${m.status === 'active' ? '#22c55e' : m.status === 'degraded' ? '#f59e0b' : '#3b82f6'}`,
                    }}
                  >
                    <div style={{ ...s.fc, gap: 10, marginBottom: 10 }}>
                      <div
                        style={{
                          fontFamily: "'Rajdhani', sans-serif",
                          fontSize: 17,
                          fontWeight: 700,
                        }}
                      >
                        {m.name}
                      </div>
                      <Badge
                        text={m.status}
                        color={m.status === 'active' ? '#22c55e' : '#f59e0b'}
                      />
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.8 }}>
                      <div>
                        Family: <span style={{ color: 'rgba(255,255,255,0.6)' }}>{m.family}</span>
                      </div>
                      <div>
                        Provider:{' '}
                        <span
                          style={{ color: providerColors[m.provider] || 'rgba(255,255,255,0.6)' }}
                        >
                          {m.provider}
                        </span>
                      </div>
                      <div>
                        Context: <span style={{ color: 'rgba(255,255,255,0.6)' }}>{m.context}</span>
                      </div>
                      <div>
                        Pricing: <span style={{ color: 'rgba(255,255,255,0.6)' }}>{m.pricing}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 10 }}>
                      {m.capabilities.map((c) => (
                        <Badge key={c} text={c} color="#D6A94E" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'usage' && (
            <div style={{ maxWidth: 1000 }}>
              <div style={{ ...s.card, overflow: 'hidden' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.5fr 1fr 1.5fr 1fr 1fr',
                    gap: 12,
                    padding: '12px 20px',
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                    fontSize: 11,
                    color: 'rgba(255,255,255,0.3)',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  <span>Model</span>
                  <span>Requests</span>
                  <span>Tokens</span>
                  <span>Cost</span>
                  <span>Avg Latency</span>
                </div>
                {USAGE.map((u) => (
                  <div
                    key={u.model}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.5fr 1fr 1.5fr 1fr 1fr',
                      gap: 12,
                      padding: '14px 20px',
                      borderBottom: '1px solid rgba(255,255,255,0.03)',
                      fontSize: 13,
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>
                      {MODELS.find((m) => m.id === u.model)?.name || u.model}
                    </span>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>
                      {u.requests.toLocaleString()}
                    </span>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>
                      {(u.tokens / 1000000).toFixed(1)}M
                    </span>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>{u.cost}</span>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>{u.latency}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'keys' && (
            <div style={{ maxWidth: 1000 }}>
              <div style={{ ...s.fc, justifyContent: 'space-between', marginBottom: 20 }}>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>
                  {API_KEYS.length} active API keys
                </p>
                <button
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg,#D6A94E,#FF6A1A)',
                    border: 'none',
                    borderRadius: 8,
                    color: '#050505',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  + Generate Key
                </button>
              </div>
              <div style={{ ...s.card, overflow: 'hidden' }}>
                {API_KEYS.map((k) => (
                  <div
                    key={k.key}
                    style={{
                      ...s.fc,
                      gap: 16,
                      padding: '16px 20px',
                      borderBottom: '1px solid rgba(255,255,255,0.03)',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{k.name}</div>
                      <div
                        style={{
                          fontFamily: "'Fira Code', monospace",
                          fontSize: 12,
                          color: 'rgba(255,255,255,0.3)',
                          marginTop: 2,
                        }}
                      >
                        {k.key}
                      </div>
                    </div>
                    <div
                      style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', textAlign: 'right' }}
                    >
                      <div>{k.usage}</div>
                      <div style={{ fontSize: 11 }}>Last: {k.lastUsed}</div>
                    </div>
                    <Badge text={k.tier} color={k.tier === 'team' ? '#8b5cf6' : '#D6A94E'} />
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)' }}>{k.created}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'providers' && (
            <div style={{ maxWidth: 1000 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: 16,
                }}
              >
                {[
                  {
                    name: 'OpenRouter',
                    desc: 'Primary inference provider — 8 models',
                    status: 'Connected',
                    color: '#3b82f6',
                    key: 'configured',
                    usage: '$15.05/day',
                  },
                  {
                    name: 'Ollama (Local)',
                    desc: 'Local model runner — 4 models',
                    status: 'Connected',
                    color: '#22c55e',
                    key: 'noneeded',
                    usage: 'Free',
                  },
                  {
                    name: 'HuggingFace Inference',
                    desc: 'HF Inference API — 0 models active',
                    status: 'Standby',
                    color: '#f59e0b',
                    key: 'configured',
                    usage: 'Not used',
                  },
                  {
                    name: 'GitHub Models',
                    desc: 'Azure-backed — blocked',
                    status: 'Blocked',
                    color: '#ef4444',
                    key: 'missing',
                    usage: 'N/A',
                  },
                  {
                    name: 'Docker Model Runner',
                    desc: 'Containerized models',
                    status: 'Standby',
                    color: '#0db7ed',
                    key: 'notconfigured',
                    usage: 'Not used',
                  },
                ].map((p) => (
                  <div key={p.name} style={{ ...s.card, padding: 20 }}>
                    <div style={{ ...s.fc, gap: 10, marginBottom: 12 }}>
                      <div
                        style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }}
                      />
                      <div style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{p.name}</div>
                      <Badge text={p.status} color={p.color} />
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.7 }}>
                      {p.desc}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 8 }}>
                      Usage: {p.usage}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'pricing' && (
            <div style={{ maxWidth: 1000 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: 16,
                }}
              >
                {[
                  {
                    tier: 'Free',
                    price: '$0',
                    models: '4 local models',
                    limits: '10 req/day API',
                    features: 'Ollama models, Embed, Search, Mini, Code',
                  },
                  {
                    tier: 'Developer',
                    price: '$20',
                    models: '9 models',
                    limits: '1,000 req/day',
                    features: 'Orion, Vega, Nova, Lyra, Atlas, Healer, Embed, Search, Mini',
                  },
                  {
                    tier: 'Team',
                    price: '$99',
                    models: '11 models',
                    limits: '5,000 req/day',
                    features: 'All Developer + Draco, Hunter, priority routing',
                  },
                  {
                    tier: 'Enterprise',
                    price: '$499',
                    models: '12 models',
                    limits: '100,000 req/day',
                    features: 'All models, dedicated endpoints, SLA, private deployment',
                  },
                ].map((p) => (
                  <div
                    key={p.tier}
                    style={{
                      ...s.card,
                      padding: 24,
                      textAlign: 'center',
                      border: p.tier === 'Developer' ? '1px solid rgba(214,169,78,0.3)' : undefined,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "'Rajdhani', sans-serif",
                        fontSize: 14,
                        fontWeight: 600,
                        letterSpacing: '2px',
                        color: p.tier === 'Developer' ? '#D6A94E' : 'rgba(255,255,255,0.4)',
                        marginBottom: 12,
                        textTransform: 'uppercase',
                      }}
                    >
                      {p.tier}
                    </div>
                    <div
                      style={{
                        fontFamily: "'Rajdhani', sans-serif",
                        fontSize: 36,
                        fontWeight: 700,
                        marginBottom: 8,
                      }}
                    >
                      {p.price}
                      <span style={{ fontSize: 14, opacity: 0.4 }}>/mo</span>
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: 'rgba(255,255,255,0.4)',
                        lineHeight: 1.8,
                        marginBottom: 16,
                      }}
                    >
                      {p.features}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        padding: '6px 12px',
                        background: 'rgba(214,169,78,0.08)',
                        borderRadius: 6,
                        color: '#D6A94E',
                        display: 'inline-block',
                      }}
                    >
                      {p.limits}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
